import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Text,
  VStack,
  Alert,
  AlertIcon,
  Flex,
} from "@chakra-ui/react";
import { Field, Formik, Form } from "formik";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { userSelector } from "store/user/selector";
import { apiService } from "network/api";
import { USER_ROLE_TYPE } from "constants/statuses";
import { setAlertMessageRequest } from "store/ui/reducer";
import * as Yup from "yup";
import { AuthInput } from "components/auth/AuthInput";
import AlertTriangleIcon from "assets/img/profile/alert-triangle.svg";

type ChangePasswordInputs = {
  current_password: string;
  password_confirmation: string;
  confirm_password: string;
};

const passwordSchema = Yup.object({
  current_password: Yup.string().required("Current password is required."),
  password_confirmation: Yup.string()
    .min(8, "The password is less than 8 characters.")
    .matches(/[A-Z]/, "The password doesn't have a capital letter.")
    .matches(/\d/, "The password doesn't have any numbers.")
    .matches(/[!@#$%^&*(),.?":{}|<>]/, "The password doesn't have special characters.")
    .required("New password is required."),
  confirm_password: Yup.string()
    .oneOf([Yup.ref("password_confirmation")], "Passwords must match.")
    .required("Please confirm your new password."),
});

// Custom password validation function from reset password
const getPasswordErrors = (newPassword: string, confirmPassword: string) => {
  const errors: string[] = [];
  
  if (newPassword.length < 8) {
    errors.push("The password is less than 8 characters.");
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
    errors.push("The password doesn't have special characters.");
  }
  if (!/\d/.test(newPassword)) {
    errors.push("The password doesn't have any numbers.");
  }
  if (!/[A-Z]/.test(newPassword)) {
    errors.push("The password doesn't have a capital letter.");
  }
  if (newPassword.length > 0 && confirmPassword.length === 0) {
    errors.push("The passwords don't match.");
  } else if (confirmPassword.length > 0 && newPassword !== confirmPassword) {
    errors.push("The passwords don't match.");
  }
  
  return errors;
};

const buttonStyle = {
  bg: "#3B82F6",
  color: "#FFF",
  fontSize: "14px",
  fontWeight: "600",
  padding: "12px 24px",
  height: "40px",
  borderRadius: "24px",
  border: "1px solid #3B82F6",
  _hover: {
    bg: "#FFF",
    color: "#3B82F6",
  },
  _active: {
    bg: "#1D4ED8",
  },
};

export default function ChangePasswordCard() {
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const isCustomer = user?.user_role_type_id === USER_ROLE_TYPE.CUSTOMER;

  const passwordInitialValues: ChangePasswordInputs = {
    current_password: "",
    password_confirmation: "",
    confirm_password: "",
  };

  const normalizeErrorMessage = (err: any): string => {
    const raw = err?.data?.messages?.current_password || err?.data?.messages?.message || err.data.message
    if (!raw) return "Failed to update password. Please try again.";
    if (typeof raw === "string") return raw;

    if (typeof raw === "object") {
      const currentPasswordError = raw?.current_password;
      if (typeof currentPasswordError === "string") return currentPasswordError;
      if (Array.isArray(currentPasswordError) && typeof currentPasswordError[0] === "string") return currentPasswordError[0];

      const firstValue = Object.values(raw).find((v) => typeof v === "string" && v.trim().length > 0);
      if (typeof firstValue === "string") return firstValue;

      const firstArrayValue = Object.values(raw).find((v) => Array.isArray(v) && typeof v[0] === "string");
      if (Array.isArray(firstArrayValue) && typeof firstArrayValue[0] === "string") return firstArrayValue[0];
    }

    return "Failed to update password. Please try again.";
  };

  const onUpdatePassword = async (values: ChangePasswordInputs, resetForm: () => void) => {
    const errors = getPasswordErrors(values.password_confirmation, values.confirm_password);
    
    if (errors.length > 0) {
      setPasswordErrors(errors);
      setTimeout(() => setPasswordErrors([]), 5000);
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        user_id: user.user_id,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        current_password: values.current_password,
        password_confirmation: values.password_confirmation,
        password: values.password_confirmation,
      };
      
      await apiService.updateUser(user.user_id, payload);
      
      setPasswordSuccess(true);
      setPasswordErrors([]);
      setNewPassword("");
      setConfirmPassword("");
      resetForm();
      setTimeout(() => setPasswordSuccess(false), 5000);
    } catch (error: any) {
      const errorMessage = normalizeErrorMessage(error);
      if (!isCustomer) {
        dispatch(
          setAlertMessageRequest({
            type: "error",
            message: errorMessage,
          })
        );
      }
      setPasswordErrors([errorMessage]);
      setTimeout(() => setPasswordErrors([]), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      bg="white"
      borderRadius="12px"
      border="1px solid #E5E7EB"
      p="22px"
      maxW="691px"
    >
      <Text fontSize="16px" fontWeight="600" color="#0B0E10" mb="4px">
        Change password
      </Text>
      <Text fontSize="14px" color="#0B0E10" mb="16px">
        You can update your password here if you need to change it.
      </Text>

      {passwordSuccess && (
        <Alert status="success" mb="16px" borderRadius="8px">
          <AlertIcon />
          Your password has been updated successfully.
        </Alert>
      )}

      {passwordErrors.length > 0 && (
        <VStack spacing="8px" mb="16px" align="stretch">
          {passwordErrors.map((error, index) => (
            <Alert key={index} fontSize="14px" status="error" borderRadius="8px" bg="#FEF2F2" color="#0B0E10">
              <img src={AlertTriangleIcon} alt="alert" width="20px" height="18px" style={{ marginRight: "8px" }} />
              {error}
            </Alert>
          ))}
        </VStack>
      )}

      {/* Inner card for form fields */}
      <Box
        bg="transparent"
        borderRadius="8px"
        border="1px solid #D2DAE5"
        p="16px"
      >
        <Formik
          initialValues={passwordInitialValues}
          validationSchema={passwordSchema}
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={(values, { resetForm }) => onUpdatePassword(values, resetForm)}
        >
          {({ errors, submitCount }) => (
            <Form>
              <VStack spacing="20px" align="stretch">
                <FormControl isInvalid={submitCount > 0 && !!errors.current_password}>
                  <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="6px">
                    Current password
                  </FormLabel>
                  <Field name="current_password">
                    {({ field }: any) => (
                      <AuthInput
                        {...field}
                        role="customer"
                        chakraVariant="authCustomerNew"
                        type="password"
                        placeholder="Enter your current password"
                        isInvalid={submitCount > 0 && !!errors.current_password}
                      />
                    )}
                  </Field>
                </FormControl>

                <FormControl isInvalid={submitCount > 0 && !!errors.password_confirmation}>
                  <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="6px">
                    New password
                  </FormLabel>
                  <Field name="password_confirmation">
                    {({ field }: any) => (
                      <AuthInput
                        {...field}
                        role="customer"
                        chakraVariant="authCustomerNew"
                        type="password"
                        placeholder="Enter your new password"
                        title="Password must be at least 8 characters and include one number."
                        isInvalid={submitCount > 0 && !!errors.password_confirmation}
                        onChange={(e: any) => {
                          field.onChange(e);
                          setNewPassword(e.target.value);
                        }}
                      />
                    )}
                  </Field>
                  <Text fontSize="12px" color="#0B0E10" mt="4px">
                    Password must be at least 8 characters and include one number.
                  </Text>
                </FormControl>

                <FormControl isInvalid={submitCount > 0 && !!errors.confirm_password}>
                  <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="6px">
                    Confirm new password
                  </FormLabel>
                  <Field name="confirm_password">
                    {({ field }: any) => (
                      <AuthInput
                        {...field}
                        role="customer"
                        chakraVariant="authCustomerNew"
                        type="password"
                        placeholder="Re-enter your new password"
                        title="Password must be at least 8 characters and include one number."
                        isInvalid={submitCount > 0 && !!errors.confirm_password}
                        onChange={(e: any) => {
                          field.onChange(e);
                          setConfirmPassword(e.target.value);
                        }}
                      />
                    )}
                  </Field>
                </FormControl>

                {/* Password validation errors display */}
                {submitCount > 0 && newPassword && getPasswordErrors(newPassword, confirmPassword).length > 0 && (
                  <Box
                    bg="#FEF2F2"
                    borderRadius="8px"
                    padding="12px 16px"
                    mt="14px"
                  >
                    <Text
                      fontSize="14px"
                      fontWeight="400"
                      color="#0B0E10"
                      mb="8px"
                    >
                      This password is invalid because:
                    </Text>
                    <Flex direction="column" gap="4px">
                      {getPasswordErrors(newPassword, confirmPassword).map((error, index) => (
                        <Flex key={index} align="flex-start" gap="12px">
                          <Text color="#B91C1C" fontSize="20px" lineHeight="1.2">
                            •
                          </Text>
                          <Text
                            fontSize="14px"
                            color="#B91C1C"
                            flex={1}
                          >
                            {error}
                          </Text>
                        </Flex>
                      ))}
                    </Flex>
                  </Box>
                )}

                <Button type="submit"  w="192px" h="40px"  sx={buttonStyle} isLoading={isLoading}>
                  Save changes
                </Button>
              </VStack>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
}
